import React, { useState } from 'react'
import { Row, Col, Alert, Button } from 'reactstrap'
import { gql, useApolloClient, useQuery } from '@apollo/client'
import CustomInputField from '../../Drivers/Driver/Detail/DetailsNew/CustomInputField'
import UpdateVehicleMakeModelPopover from './UpdateVehicleMakeModelPopover'
import { HasPermissionsQuery } from "../../Functions/querys"

const UpdateIncidentPartyVehicle = gql`
  mutation updateIncidentPartyVehicleMutation($input: UpdateIncidentPartyVehicleMutationInput!) {
    updateIncidentPartyVehicle(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const VehicleInfo = ({ vehicle, refetchQuery, isDefault }) => {
    const apolloClient = useApolloClient()
    const [editField, setEditField] = useState('')
    const [inputValues, setInputValues] = useState({})
    const [originalValues, setOriginalValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [fieldLoading, setFieldLoading] = useState({})
    const [error, setError] = useState(null)
    const [makeModelPopoverOpen, setMakeModelPopoverOpen] = useState(false)
    const { data: permissionsData } = useQuery(HasPermissionsQuery, {
        variables: {
            userPermissions: ["change_incident_sensitive_data"],
        },
    })
    const userPermissions = permissionsData?.hasPermissions || []

    const handleMakeModelSave = async (make, model) => {
        await updateVehicleHandler(vehicle.id, 'make', make)
        await updateVehicleHandler(vehicle.id, 'model', model)
        setMakeModelPopoverOpen(false)
    }

    const editFieldHandler = (fieldName, fieldValue = '') => {
        setEditField(fieldName)
        setInputValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setOriginalValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setError(null)
    }

    const cancelEditHandler = () => {
        if (!editField) return
        setEditField('')
        setInputValues({})
        setOriginalValues({})
        setFieldErrors({})
        setFieldLoading({})
        setError('')
    }

    const updateInputHandler = (e) => {
        const rawValue = e.target.value
        const fieldName = e.target.name
        const fieldType = e.target.type
        setInputValues(prev => ({
            ...prev,
            [fieldName]: fieldType === 'select' ? rawValue === 'true' ? true : rawValue === 'false' ? false : rawValue : rawValue,
        }))
    }

    const saveFieldHandler = (partyVehicleId, fieldName, fieldValue) => {
        if (fieldErrors[fieldName]) return
        if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
            cancelEditHandler()
            return
        }
        updateVehicleHandler(partyVehicleId, fieldName, fieldValue)
    }

    const updateVehicleHandler = async (partyVehicleId, name, value) => {
        try {
            const trimmedValue = typeof value === 'string' ? value.trim() : value
            if (trimmedValue === originalValues[name]) {
                cancelEditHandler()
                return
            }

            const isValidValue = typeof trimmedValue === 'boolean' || Boolean(trimmedValue) || trimmedValue === ''
            if (!partyVehicleId || !name || !isValidValue) return

            setFieldLoading(prev => ({ ...prev, [name]: true }))
            setError('')

            const input = {
                partyVehicleId: partyVehicleId,
                [name]: trimmedValue,
            }

            try {
                const result = await apolloClient.mutate({
                    mutation: UpdateIncidentPartyVehicle,
                    variables: { input },
                })

                const { ok, errors } = result?.data?.updateIncidentPartyVehicle || {}

                if (ok) {
                    setEditField('')
                    setError('')
                    setInputValues(prev => ({ ...prev, [name]: trimmedValue }))
                    setOriginalValues(prev => ({ ...prev, [name]: trimmedValue }))
                    refetchQuery && refetchQuery()
                } else {
                    const errorMessage =
                        errors?.[0]?.messages?.toString() ||
                        'Something went wrong, Please contact admin for support!'
                    setError(errorMessage)
                }
            } catch (err) {
                setError(err.message)
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setEditField('')
            setFieldLoading(prev => ({ ...prev, [name]: false }))
            setFieldErrors(prev => ({ ...prev, [name]: '' }))
        }
    }

    return (
        vehicle &&
        <Row key={vehicle?.id}>
            {error && <Col xs={12}><Alert color="danger">{error}</Alert></Col>}
            <Col xs={3} className="mb-2" id="editMakeModelBtn">
                <CustomInputField
                    label="Make & Model"
                    fieldName="makeModel"
                    inputType="text"
                    currentValue={`${vehicle.make || "--"} - ${vehicle.model || "--"}`}
                    showEditIcon={!isDefault}
                    showCopyIcon={false}
                    onEdit={() => setMakeModelPopoverOpen(true)}
                    renderEditingModal={() =>
                        makeModelPopoverOpen && (
                            <UpdateVehicleMakeModelPopover
                                open={makeModelPopoverOpen}
                                target="editMakeModelBtn"
                                id="editMakeModelBtn"
                                vehicle={vehicle}
                                onSave={handleMakeModelSave}
                                onCancel={() => setMakeModelPopoverOpen(false)}
                            />
                        )
                    }
                />
            </Col>

            <Col xs={3} className="mb-2">
                <CustomInputField
                    label="Year"
                    fieldName="year"
                    showCopyIcon={false}
                    showEditIcon={!isDefault}
                    inputType="text"
                    isEditing={editField === 'year'}
                    isLoading={!!fieldLoading?.year}
                    inputValue={inputValues?.year}
                    currentValue={vehicle?.year}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(vehicle.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                />
            </Col>
            <Col xs={3} className="mb-2">
                <CustomInputField
                    label="VIN"
                    fieldName="vin"
                    showCopyIcon={false}
                    showEditIcon={!isDefault}
                    inputType="text"
                    isEditing={editField === 'vin'}
                    isLoading={!!fieldLoading?.vin}
                    inputValue={inputValues?.vin}
                    currentValue={vehicle?.vin}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(vehicle.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                />
            </Col>

            <Col xs={3} className="mb-2">
                <CustomInputField
                    label="Plate Number"
                    fieldName="plateNumber"
                    showCopyIcon={false}
                    showEditIcon={!isDefault || userPermissions.includes("change_incident_sensitive_data")}
                    inputType="text"
                    isEditing={editField === 'plateNumber'}
                    isLoading={!!fieldLoading?.plateNumber}
                    inputValue={inputValues?.plateNumber}
                    currentValue={vehicle?.plateNumber}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(vehicle.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                />
            </Col>

            <Col xs={3} className="mb-2">
                <CustomInputField
                    label="Insurance Company"
                    fieldName="insuranceCompany"
                    showCopyIcon={false}
                    showEditIcon={!isDefault || userPermissions.includes("change_incident_sensitive_data")}
                    inputType="text"
                    isEditing={editField === 'insuranceCompany'}
                    isLoading={!!fieldLoading?.insuranceCompany}
                    inputValue={inputValues?.insuranceCompany}
                    currentValue={vehicle?.insuranceCompany}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(vehicle.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                />
            </Col>

            <Col xs={3} className="mb-2">
                <CustomInputField
                    label="Policy Number"
                    fieldName="policyNumber"
                    showCopyIcon={false}
                    showEditIcon={!isDefault || userPermissions.includes("change_incident_sensitive_data")}
                    inputType="text"
                    isEditing={editField === 'policyNumber'}
                    isLoading={!!fieldLoading?.policyNumber}
                    inputValue={inputValues?.policyNumber}
                    currentValue={vehicle?.policyNumber}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(vehicle.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                />
            </Col>

            <Col xs={12} className="mb-2">
                <CustomInputField
                    label="Damage Description"
                    fieldName="damageDescription"
                    showCopyIcon={false}
                    showEditIcon={true}
                    inputType="text"
                    isEditing={editField === 'damageDescription'}
                    isLoading={!!fieldLoading?.damageDescription}
                    inputValue={inputValues?.damageDescription}
                    currentValue={vehicle?.damageDescription}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(vehicle.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                />
            </Col>
        </Row>
    )
}

export default VehicleInfo
